import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import {
  isValidManagerResponse,
  isValidManagersResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';

import type { Manager, WithOptionalId, PagedResponse } from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { getDefaultError } from '../../utils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

// NOTE: Developer-only impersonation helper. Do not expose to general UI flows.
export const fetchAllManagers = async (): Promise<FetchResult<Manager[]>> => {
  const response = await getDataWithRetry<PagedResponse<Manager>>(
    `${apiBaseUrl}managers?page=0&size=1000&sort=firstName`,
    {},
    isValidManagersResponse,
  );
  return mapPagedResult(response);
};

export const createNewManager = async (
  manager: WithOptionalId<Manager>,
  gymId: string,
): Promise<FetchResult<Manager | null>> => {
  if (!gymId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No Gym Provided'),
    };
  }
  const createResponse = await getDataWithRetry<Manager | null>(
    `${apiBaseUrl}managers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(manager),
    },
    isValidManagerResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }

  const associateResult = await sendWithoutBody(
    `${apiBaseUrl}gyms/${gymId}/associate/managers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );
  if (!associateResult.success) {
    return associateResult;
  }
  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      gymIds: [gymId],
    },
  };
};

export const updateManagerData = async (
  manager: Manager,
): Promise<FetchResult<Manager | null>> => {
  if (!manager?.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No manager id provided'),
    };
  }
  const updateResponse = await getDataWithRetry<Manager | null>(
    `${apiBaseUrl}managers`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(manager),
    },
    isValidManagerResponse,
  );
  if (!updateResponse.success) {
    return updateResponse;
  }
  return updateResponse;
};

export const fetchManagerById = (
  managerId: string,
): Promise<FetchResult<Manager | null>> => {
  return getDataWithRetry(
    `${apiBaseUrl}managers/${managerId}`,
    {},
    isValidManagerResponse,
  );
};

export const getGymManagers = async (
  gymId: string,
): Promise<FetchResult<Manager[]>> => {
  const response = await getDataWithRetry<PagedResponse<Manager>>(
    `${apiBaseUrl}managers/gym/${gymId}?page=0&size=1000&sort=firstName`,
    {},
    isValidManagersResponse,
  );
  return mapPagedResult(response);
};

export const fetchManagersByIds = async (
  managerIds: string[],
): Promise<FetchResult<Manager[]>> => {
  if (managerIds.length === 0) {
    return createSuccessResult<Manager[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Manager>>(
    `${apiBaseUrl}managers/getByIds?page=0&size=1000&sort=firstName`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(managerIds),
    },
    isValidManagersResponse,
  );
  return mapPagedResult(response);
};
